/**
 * LeagueDivisionsPage — /league/:leagueId/divisions
 * ─────────────────────────────────────────────────────────────────
 * Manage divisions within a season. Divisions are per-season because
 * structure changes year to year (5 teams in spring → one division;
 * 10 teams in fall → two divisions, etc.).
 *
 * Phase 2b chicken-and-egg problem: divisions are scoped to a
 * season, but Phase 2c is where full season management lands. This
 * page solves the gap pragmatically:
 *   • If the league has zero seasons, show a quick-create form.
 *     Once a season exists, divisions can be managed.
 *   • If seasons exist, render a dropdown picker. Default to the
 *     active season, or the most recent if none is active.
 *
 * The minimal "create season" form here will be replaced by the
 * Phase 2c wizard. The backend endpoint (POST /seasons) is the
 * same in both cases, so this isn't throwaway code — just a
 * simpler UI on top of the same API.
 *
 * Permissions:
 *   • Viewing seasons + divisions: any league member.
 *   • Creating a season:   manage_league_settings (matches Phase 1).
 *   • Creating/editing/deleting divisions: manage_divisions.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Layers, Plus, Save, Trash2, Edit2, Calendar, ArrowRight,
} from 'lucide-react';
import api from '../../services/api.js';
import toast from 'react-hot-toast';
import LeagueSubNav from './LeagueSubNav.jsx';
import { useLeagueMembership } from '../../hooks/useLeagueMembership.js';

export default function LeagueDivisionsPage() {
  const { leagueId } = useParams();
  const { isManager, can, loading: roleLoading } = useLeagueMembership(leagueId);
  const canManageDivisions = isManager || can('manage_divisions');
  const canCreateSeasons   = isManager || can('manage_league_settings');

  const [league, setLeague]   = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [divisions, setDivisions] = useState([]);
  const [loading, setLoading]     = useState(true);
  // Phase 2c — Seasons tab is the authoritative place to create
  // seasons; we link there rather than duplicating the wizard here.

  // Pick a sensible default season: prefer 'active', else most-recent by start_date.
  const defaultSeasonId = useMemo(() => {
    if (seasons.length === 0) return '';
    const active = seasons.find(s => s.status === 'active');
    if (active) return active.id;
    // seasons are returned start_date DESC by the backend, so first is most recent.
    return seasons[0].id;
  }, [seasons]);

  async function loadShell() {
    if (!leagueId) return;
    try {
      const [leagueRes, seasonsRes] = await Promise.all([
        api.get(`/leagues/${leagueId}`),
        api.get(`/leagues/${leagueId}/seasons`),
      ]);
      setLeague(leagueRes?.data?.league || null);
      setSeasons(seasonsRes?.data?.seasons || []);
    } catch (err) {
      if (err?.response?.status === 403) toast.error('You are not a member of this league');
      else if (err?.response?.status !== 401) toast.error('Could not load seasons');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadShell(); /* eslint-disable-next-line */ }, [leagueId]);

  // When seasons load (or change), pick the default if nothing's selected.
  useEffect(() => {
    if (!selectedSeasonId && defaultSeasonId) setSelectedSeasonId(defaultSeasonId);
  }, [defaultSeasonId, selectedSeasonId]);

  async function loadDivisions(seasonId) {
    if (!seasonId) { setDivisions([]); return; }
    try {
      const { data } = await api.get(`/leagues/${leagueId}/seasons/${seasonId}/divisions`);
      setDivisions(data?.divisions || []);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error('Could not load divisions');
    }
  }
  useEffect(() => {
    if (selectedSeasonId) loadDivisions(selectedSeasonId);
    /* eslint-disable-next-line */
  }, [selectedSeasonId]);

  if (loading || roleLoading) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 820 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>Loading…</div>
      </div>
    );
  }
  if (!league) {
    return (
      <div className="animate-fade-in" style={{ maxWidth: 820 }}>
        <div className="card" style={{ color: 'var(--text-2)', textAlign: 'center' }}>
          League not found or no access.
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" style={{ maxWidth: 820 }}>
      <LeagueSubNav league={league} />

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 className="section-title text-xl">Divisions</h2>
          <p style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4 }}>
            Group teams into divisions per season. Each season can have its own division layout.
          </p>
        </div>
      </div>

      {seasons.length === 0 ? (
        <EmptyNoSeasonsCard
          canCreate={canCreateSeasons}
          leagueId={leagueId}
        />
      ) : (
        <>
          <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Calendar size={16} color="var(--ice)" />
            <span style={{ fontSize: 13, fontWeight: 600 }}>Season:</span>
            <select
              value={selectedSeasonId}
              onChange={(e) => setSelectedSeasonId(e.target.value)}
              style={{
                ...inputStyle,
                width: 'auto', minWidth: 200, flex: '1 1 240px',
              }}
            >
              {seasons.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.status})
                </option>
              ))}
            </select>
            {canCreateSeasons && (
              <Link to={`/league/${leagueId}/seasons`}
                className="btn-ghost"
                style={{ ...tinyBtn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
                <Plus size={11} style={{ marginRight: 4 }} />
                New season
                <ArrowRight size={10} style={{ marginLeft: 4 }} />
              </Link>
            )}
          </div>

          {selectedSeasonId && (
            <DivisionsList
              leagueId={leagueId}
              seasonId={selectedSeasonId}
              divisions={divisions}
              canManage={canManageDivisions}
              onChange={() => loadDivisions(selectedSeasonId)}
            />
          )}
        </>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Empty states
// ───────────────────────────────────────────────────────────────

function EmptyNoSeasonsCard({ canCreate, leagueId }) {
  return (
    <div className="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
      <Calendar size={40} color="var(--text-2)" style={{ margin: '0 auto 14px', opacity: 0.5 }} />
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>No seasons yet</div>
      <div style={{ fontSize: 13, color: 'var(--text-2)', maxWidth: 420, margin: '0 auto 18px' }}>
        Divisions are per-season because the structure changes year over year. Create a season first
        from the Seasons tab, then come back here to set up divisions.
      </div>
      {canCreate ? (
        <Link to={`/league/${leagueId}/seasons`} className="btn-ice"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} />
          Go to Seasons
          <ArrowRight size={12} />
        </Link>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
          A manager or someone with <code>manage_league_settings</code> can create the first season.
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Divisions list (under a chosen season)
// ───────────────────────────────────────────────────────────────

function DivisionsList({ leagueId, seasonId, divisions, canManage, onChange }) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', display_order: 0 });

  function startAdd() {
    setForm({ name: '', display_order: divisions.length });
    setAdding(true);
    setEditingId(null);
  }
  function startEdit(d) {
    setForm({ name: d.name, display_order: d.display_order });
    setEditingId(d.id);
    setAdding(false);
  }
  function cancel() {
    setAdding(false);
    setEditingId(null);
  }
  async function save() {
    const payload = {
      name: form.name.trim(),
      display_order: Number.isInteger(form.display_order) ? form.display_order : 0,
    };
    if (!payload.name) {
      toast.error('Name is required');
      return;
    }
    try {
      if (editingId) {
        await api.patch(`/leagues/${leagueId}/seasons/${seasonId}/divisions/${editingId}`, payload);
        toast.success('Division updated');
      } else {
        await api.post(`/leagues/${leagueId}/seasons/${seasonId}/divisions`, payload);
        toast.success('Division added');
      }
      cancel();
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not save');
    }
  }
  async function remove(d) {
    const ok = window.confirm(
      d.team_count > 0
        ? `Delete "${d.name}"? ${d.team_count} team${d.team_count === 1 ? '' : 's'} will become unassigned.`
        : `Delete "${d.name}"?`
    );
    if (!ok) return;
    try {
      await api.delete(`/leagues/${leagueId}/seasons/${seasonId}/divisions/${d.id}`);
      toast.success('Division deleted');
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not delete');
    }
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
          {divisions.length} division{divisions.length === 1 ? '' : 's'} in this season
        </span>
        {canManage && !adding && !editingId && (
          <button type="button" className="btn-ice" onClick={startAdd} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={13} />
            Add division
          </button>
        )}
      </div>

      {divisions.length === 0 && !adding ? (
        <div className="card" style={{ textAlign: 'center', padding: 32, color: 'var(--text-2)', fontSize: 13 }}>
          <Layers size={32} style={{ margin: '0 auto 10px', opacity: 0.5 }} />
          No divisions yet. Add one to group teams in this season.
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {divisions.map((d, i) => (
            editingId === d.id ? (
              <div key={d.id} style={{
                padding: 14, borderBottom: i === divisions.length - 1 ? 'none' : '1px solid var(--border)',
                background: 'var(--surface-2)',
              }}>
                <DivisionEditFields form={form} setForm={setForm} onCancel={cancel} onSave={save} />
              </div>
            ) : (
              <div key={d.id} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '12px 16px',
                borderBottom: i === divisions.length - 1 ? 'none' : '1px solid var(--border)',
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: 'var(--ice-a10)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Layers size={14} color="var(--ice)" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{d.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>
                    {d.team_count} team{d.team_count === 1 ? '' : 's'}
                    {' · '}
                    Order: {d.display_order}
                  </div>
                </div>
                {canManage && (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button type="button" className="btn-ghost" style={tinyBtn} onClick={() => startEdit(d)}>
                      <Edit2 size={11} style={{ marginRight: 4 }} />
                      Edit
                    </button>
                    <button type="button" className="btn-ghost"
                      style={{ ...tinyBtn, color: 'var(--penalty)' }} onClick={() => remove(d)}>
                      <Trash2 size={11} />
                    </button>
                  </div>
                )}
              </div>
            )
          ))}
          {adding && (
            <div style={{ padding: 14, background: 'var(--surface-2)' }}>
              <DivisionEditFields form={form} setForm={setForm} onCancel={cancel} onSave={save} />
            </div>
          )}
        </div>
      )}
    </>
  );
}

function DivisionEditFields({ form, setForm, onCancel, onSave }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <input type="text" value={form.name} maxLength={60}
        onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
        placeholder="Division name (e.g. A Division, East)" autoFocus
        style={inputStyle} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Display order:</span>
        <input type="number" value={form.display_order} min={0}
          onChange={(e) => setForm(f => ({ ...f, display_order: parseInt(e.target.value, 10) || 0 }))}
          style={{ ...inputStyle, width: 80 }} />
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>(lower numbers shown first)</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" className="btn-ghost" style={tinyBtn} onClick={onCancel}>Cancel</button>
        <button type="button" className="btn-ice" style={tinyBtn} onClick={onSave} disabled={!form.name.trim()}>
          <Save size={11} style={{ marginRight: 4 }} />
          Save
        </button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Shared bits
// ───────────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '8px 12px',
  color: 'var(--text)',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  outline: 'none',
};
const fieldLabelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 6 };
const tinyBtn = { padding: '4px 10px', fontSize: 11, minHeight: 'auto' };
